import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Mail, Hash, Calendar, MapPin, Users, FileText, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_URL from '../api';

function Register() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const { darkMode } = useTheme();
  const user = JSON.parse(localStorage.getItem('user'));

  const bg = darkMode ? '#0f0f1a' : '#f0f2f5';
  const card = darkMode ? '#1a1a2e' : '#fff';
  const border = darkMode ? 'rgba(255,255,255,0.07)' : '#f0f0f0';
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    axios.get(`${API_URL}/api/events/${eventId}`).then(res => setEvent(res.data));
  }, [eventId]);

  const handleSubmit = async () => {
    if (!agreed) return;
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/registrations`,
        { eventId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/ticket/${res.data._id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong!');
    }
    setLoading(false);
  };

  if (!event) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', background: bg }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid #e94560', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const cardStyle = { background: card, borderRadius: '16px', padding: '20px', marginBottom: '16px', border: `1px solid ${border}`, boxShadow: darkMode ? '0 2px 15px rgba(0,0,0,0.2)' : '0 2px 10px rgba(0,0,0,0.05)' };

  return (
    <div style={{ background: bg, minHeight: '100vh', padding: '30px 20px' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div style={{ maxWidth: '520px', margin: '0 auto', animation: 'fadeIn 0.4s ease' }}>
        <div style={{ marginBottom: '25px' }}>
          <h1 style={{ color: text, fontSize: '26px', fontWeight: '700' }}>Register for Event</h1>
          <p style={{ color: subtext, marginTop: '5px' }}>Securing your spot for <strong style={{ color: '#e94560' }}>{event.title}</strong></p>
        </div>
        <div style={cardStyle}>
          <h3 style={{ color: text, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}>
            <User size={16} color="#e94560" /> Your Details
          </h3>
          {[
            { icon: <User size={14} color="#e94560" />, label: 'Name', value: user?.name },
            { icon: <Mail size={14} color="#e94560" />, label: 'Email', value: user?.email },
            { icon: <Hash size={14} color="#e94560" />, label: 'Roll No', value: user?.rollNo || 'Not set' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8f8f8', borderRadius: '8px', marginBottom: '8px' }}>
              {item.icon}
              <span style={{ color: subtext, fontSize: '13px', minWidth: '55px' }}>{item.label}:</span>
              <span style={{ color: text, fontSize: '13px', fontWeight: '500' }}>{item.value}</span>
            </div>
          ))}
        </div>
        <div style={cardStyle}>
          <h3 style={{ color: text, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}>
            <FileText size={16} color="#e94560" /> Event Details
          </h3>
          {[
            { icon: <Calendar size={14} color="#e94560" />, value: new Date(event.date).toDateString() },
            { icon: <MapPin size={14} color="#e94560" />, value: event.venue },
            { icon: <Users size={14} color="#e94560" />, value: `${event.totalSeats - event.registeredCount} seats remaining` },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8f8f8', borderRadius: '8px', marginBottom: '8px' }}>
              {item.icon}
              <span style={{ color: text, fontSize: '13px' }}>{item.value}</span>
            </div>
          ))}
        </div>
        {event.rules && (
          <div style={{ ...cardStyle, background: darkMode ? 'rgba(255,152,0,0.08)' : '#fff8f0', border: `1px solid ${darkMode ? 'rgba(255,152,0,0.2)' : '#ffe082'}` }}>
            <h3 style={{ color: '#ff9800', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}>📋 Event Rules & Guidelines</h3>
            <p style={{ color: darkMode ? '#ccc' : '#555', fontSize: '14px', lineHeight: '1.7', whiteSpace: 'pre-line' }}>{event.rules}</p>
          </div>
        )}
        <div style={{ ...cardStyle, cursor: 'pointer', border: agreed ? '1.5px solid #e94560' : `1px solid ${border}` }} onClick={() => setAgreed(!agreed)}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '5px', border: `2px solid ${agreed ? '#e94560' : '#ccc'}`, background: agreed ? '#e94560' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px', transition: '0.2s' }}>
              {agreed && <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>✓</span>}
            </div>
            <p style={{ color: text, fontSize: '14px', lineHeight: '1.6' }}>I have read and agree to the event rules and guidelines. I understand that my registration may be cancelled if I violate any rules.</p>
          </div>
        </div>
        {error && (
          <div style={{ background: darkMode ? 'rgba(233,69,96,0.15)' : '#ffebee', border: '1px solid rgba(233,69,96,0.3)', borderRadius: '10px', padding: '12px', marginBottom: '16px', color: '#e94560', fontSize: '14px' }}>❌ {error}</div>
        )}
        <button onClick={handleSubmit} disabled={loading || !agreed} style={{ background: agreed ? 'linear-gradient(135deg, #e94560, #c62a47)' : (darkMode ? 'rgba(255,255,255,0.05)' : '#e0e0e0'), color: agreed ? '#fff' : subtext, width: '100%', padding: '16px', fontSize: '16px', borderRadius: '12px', cursor: agreed ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: '0.3s' }}>
          {loading ? '⏳ Registering...' : agreed ? (<>Get My Ticket <ArrowRight size={18} /></>) : '☑️ Please agree to rules first'}
        </button>
      </div>
    </div>
  );
}

export default Register;